/**
 * Hero Section Animation
 * A p5.js sketch that visualizes consciousness as flowing particles
 * with reflective properties, representing the Theory of Sovereign Reflectivity
 */

export function setupHeroAnimation(p) {
    // Configuration
    const config = {
        particleCount: 150,
        particleSize: { min: 2, max: 6 },
        particleSpeed: { min: 0.3, max: 1.2 },
        particleOpacity: { min: 0.2, max: 0.7 },
        connectionDistance: 150,
        connectionOpacity: 0.15,
        colors: {
            primary: [44, 62, 80], // #2C3E50 (Deep indigo)
            secondary: [142, 68, 173], // #8E44AD (Cosmic purple)
            accent: [241, 196, 15], // #F1C40F (Gold)
            background: [18, 18, 18] // #121212 (Near-black)
        },
        mouseInfluenceRadius: 200,
        mouseForce: 0.5
    };

    // Particle class
    class Particle {
        constructor() {
            this.reset();
            // Start particles at random positions
            this.x = p.random(p.width);
            this.y = p.random(p.height);
            this.size = p.random(config.particleSize.min, config.particleSize.max);

            // Assign a color type to each particle
            const colorRoll = p.random(100);
            if (colorRoll < 70) {
                this.color = config.colors.primary;
            } else if (colorRoll < 95) {
                this.color = config.colors.secondary;
            } else {
                this.color = config.colors.accent;
            }

            this.opacity = p.random(config.particleOpacity.min, config.particleOpacity.max);
        }

        reset() {
            // Position and movement
            this.x = -100; // Start off-screen
            this.y = p.random(p.height);
            this.vx = p.random(config.particleSpeed.min, config.particleSpeed.max);
            this.vy = p.random(-0.1, 0.1);

            // Visual properties
            this.size = p.random(config.particleSize.min, config.particleSize.max);
            this.opacity = p.random(config.particleOpacity.min, config.particleOpacity.max);
        }

        update() {
            // Move the particle
            this.x += this.vx;
            this.y += this.vy;

            // Mouse influence - particles are attracted to mouse position
            if (p.mouseX > 0 && p.mouseY > 0) {
                const dx = p.mouseX - this.x;
                const dy = p.mouseY - this.y;
                const distance = p.sqrt(dx * dx + dy * dy);

                if (distance < config.mouseInfluenceRadius) {
                    const force = (config.mouseInfluenceRadius - distance) / config.mouseInfluenceRadius * config.mouseForce;
                    this.vx += (dx / distance) * force;
                    this.vy += (dy / distance) * force;
                }
            }

            // Add a slight vertical drift to simulate consciousness flow
            this.vy += p.random(-0.01, 0.01);

            // Limit velocity
            const maxSpeed = config.particleSpeed.max * 1.5;
            const speed = p.sqrt(this.vx * this.vx + this.vy * this.vy);
            if (speed > maxSpeed) {
                this.vx = (this.vx / speed) * maxSpeed;
                this.vy = (this.vy / speed) * maxSpeed;
            }

            // Reset if off-screen
            if (this.x > p.width + 100 || this.x < -100 || this.y > p.height + 100 || this.y < -100) {
                this.reset();
            }
        }

        draw() {
            p.noStroke();
            p.fill(this.color[0], this.color[1], this.color[2], this.opacity * 255);
            p.ellipse(this.x, this.y, this.size);
        }
    }

    // Array to hold particles
    let particles = [];

    // p5.js setup function
    p.setup = function () {
        p.createCanvas(p.windowWidth, p.windowHeight);
        p.background(config.colors.background);

        // Create particles
        for (let i = 0; i < config.particleCount; i++) {
            particles.push(new Particle());
        }
    };

    // p5.js draw function
    p.draw = function () {
        p.background(config.colors.background[0], config.colors.background[1], config.colors.background[2], 20);

        // Draw connections between particles (representing reflective relationships)
        p.stroke(255, 255, 255, config.connectionOpacity * 255);
        for (let i = 0; i < particles.length; i++) {
            for (let j = i + 1; j < particles.length; j++) {
                const dx = particles[i].x - particles[j].x;
                const dy = particles[i].y - particles[j].y;
                const distance = p.sqrt(dx * dx + dy * dy);

                if (distance < config.connectionDistance) {
                    const opacity = p.map(distance, 0, config.connectionDistance, config.connectionOpacity, 0);
                    p.stroke(255, 255, 255, opacity * 255);
                    p.line(particles[i].x, particles[i].y, particles[j].x, particles[j].y);
                }
            }
        }

        // Update and draw particles
        for (let particle of particles) {
            particle.update();
            particle.draw();
        }

        // Add a subtle glow effect in the center to represent consciousness
        const centerX = p.width / 2;
        const centerY = p.height / 2;
        const radius = p.min(p.width, p.height) * 0.4;

        p.noFill();
        for (let i = 0; i < 5; i++) {
            const alpha = p.map(i, 0, 5, 0.1, 0);
            p.stroke(config.colors.secondary[0], config.colors.secondary[1], config.colors.secondary[2], alpha * 255);
            p.ellipse(centerX, centerY, radius * (1 + i * 0.2));
        }
    };

    // Handle window resize
    p.windowResized = function () {
        p.resizeCanvas(p.windowWidth, p.windowHeight);
    };
}
